import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArcButton } from "@/components/ArcButton";
import { Plus, Pencil, Trash2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { label: "Pending Review", icon: Clock, cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  published: { label: "Published", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  rejected: { label: "Rejected", icon: XCircle, cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

export default function EditorDashboard() {
  const { user } = useAuth();
  const [drafts, setDrafts] = useState([]);

  const load = () => api.get("/drafts").then((r) => setDrafts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (slug) => {
    try { await api.delete(`/articles/${slug}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif-arc text-3xl font-black">Editor Workspace</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--arc-text-2)" }}>Welcome, {user?.name}. Draft articles here — an admin reviews them before publication.</p>
        </div>
        <ArcButton to="/editor/articles/new" variant="zyfley" data-testid="editor-new-article"><Plus size={16} /> New Draft</ArcButton>
      </div>

      <div className="mt-8 space-y-3">
        {drafts.length === 0 && (
          <div className="rounded-md border p-10 text-center" style={{ backgroundColor: "var(--arc-card)" }}>
            <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>You haven't drafted any articles yet.</p>
            <div className="mt-4 flex justify-center"><ArcButton to="/editor/articles/new" variant="zyfley"><Plus size={16} /> Write your first draft</ArcButton></div>
          </div>
        )}
        {drafts.map((d) => {
          const st = STATUS[d.status] || STATUS.draft;
          return (
            <div key={d.slug} data-testid={`draft-row-${d.slug}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-serif-arc font-bold">{d.title}</span>
                  <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${st.cls}`}><st.icon size={12} /> {st.label}</span>
                  <span className={`text-xs ${d.encyclopedia === "ghoulax" ? "ghoulax-accent" : "zyfley-accent"}`}>{d.encyclopedia}</span>
                </div>
                <p className="mt-1 line-clamp-1 text-sm" style={{ color: "var(--arc-text-2)" }}>{d.description}</p>
              </div>
              <div className="flex gap-2">
                {d.status === "published" && <Link to={`/article/${d.slug}`} className="rounded border px-3 py-1.5 text-sm">View</Link>}
                <Link to={`/editor/articles/${d.slug}`} data-testid={`edit-draft-${d.slug}`} className="flex h-9 w-9 items-center justify-center rounded border"><Pencil size={15} /></Link>
                <button onClick={() => del(d.slug)} data-testid={`delete-draft-${d.slug}`} className="flex h-9 w-9 items-center justify-center rounded border text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
